import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Car, Home as HomeIcon, Heart, Briefcase, Building2, Truck, Shield,
  ArrowRight, ArrowLeft, Check, Phone, FileCheck, User, Mail, MapPin,
  Bike, Anchor, Zap, Scale, HelpCircle
} from 'lucide-react';
import { submitQuoteForm } from '../data/mock';
import { toast } from 'sonner';

const insuranceTypes = [
  { id: 'auto', label: 'Automobile', icon: Car, color: 'from-blue-500 to-blue-600' },
  { id: 'moto', label: '2 Roues / Moto', icon: Bike, color: 'from-orange-500 to-orange-600' },
  { id: 'habitation', label: 'Habitation', icon: HomeIcon, color: 'from-emerald-500 to-emerald-600' },
  { id: 'sante', label: 'Santé', icon: Heart, color: 'from-rose-500 to-rose-600' },
  { id: 'pro', label: 'Professionnelle', icon: Briefcase, color: 'from-amber-500 to-amber-600' },
  { id: 'decennale', label: 'Décennale', icon: Building2, color: 'from-violet-500 to-violet-600' },
  { id: 'flotte', label: 'Flotte Auto', icon: Truck, color: 'from-slate-500 to-slate-600' },
  { id: 'bateau', label: 'Bateau / Jet Ski', icon: Anchor, color: 'from-cyan-500 to-cyan-600' },
  { id: 'trottinette', label: 'Trottinette / NVEI', icon: Zap, color: 'from-lime-500 to-lime-600' },
  { id: 'juridique', label: 'Protection Juridique', icon: Scale, color: 'from-indigo-500 to-indigo-600' },
  { id: 'divers', label: 'Autre demande', icon: HelpCircle, color: 'from-gray-500 to-gray-600' },
];

export const Quote = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    insuranceType: searchParams.get('type') || '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    details: ''
  });

  useEffect(() => {
    const type = searchParams.get('type');
    if (type && insuranceTypes.find(t => t.id === type)) {
      setFormData(prev => ({ ...prev, insuranceType: type }));
      setStep(2);
    }
  }, [searchParams]);

  const handleTypeSelect = (typeId) => {
    setFormData({ ...formData, insuranceType: typeId });
    setStep(2);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const backendFormData = {
        insurance_type: formData.insuranceType,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: `${formData.address}, ${formData.postalCode} ${formData.city}`,
        details: formData.details
      };
      
      const result = await submitQuoteForm(backendFormData);
      if (result.success) {
        toast.success(result.message);
        setStep(4); // Success step
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez nous appeler au 05 82 81 91 20.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedType = insuranceTypes.find(t => t.id === formData.insuranceType);
  const displayLabel = selectedType?.id === 'divers' ? 'Autre demande' : selectedType?.label;
  const progress = step === 1 ? 33 : step === 2 ? 66 : step === 3 ? 100 : 100;

  return (
    <>
      <Helmet>
        <title>Devis Assurance Gratuit | Cabinet CAMBC Conseils Moissac</title>
        <meta name="description" content="Demandez votre devis assurance gratuit en ligne. Réponse sous 24h. Auto, habitation, santé, pro, décennale." />
        <link rel="canonical" href="https://www.cambc-conseils.fr/quote" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-12 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/20 rounded-full text-[#C5A059] text-sm font-medium mb-6">
              <FileCheck size={16} />
              <span>Réponse sous 24h</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              Demande de devis gratuit
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Obtenez une proposition personnalisée en quelques clics
            </p>

            {/* Progress Bar */}
            {step < 4 && (
              <div className="mt-8 max-w-md mx-auto">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className={step >= 1 ? 'text-[#C5A059]' : 'text-slate-500'}>Type</span>
                  <span className={step >= 2 ? 'text-[#C5A059]' : 'text-slate-500'}>Coordonnées</span>
                  <span className={step >= 3 ? 'text-[#C5A059]' : 'text-slate-500'}>Détails</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4B06A] transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Form Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Step 1: Select Insurance Type */}
            {step === 1 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-bold text-[#0F2137] text-center mb-8">
                  Quel type d'assurance recherchez-vous ?
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {insuranceTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleTypeSelect(type.id)}
                      className={`group p-5 bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-lg text-left ${
                        type.id === 'divers' 
                          ? 'border-dashed border-slate-300 hover:border-[#C5A059]' 
                          : 'border-slate-100 hover:border-[#C5A059]'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <type.icon size={22} className="text-white" />
                      </div>
                      <h3 className="font-semibold text-[#0F2137] text-sm sm:text-base">{type.label}</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {type.id === 'divers' ? 'Besoin spécifique' : 'Obtenir un devis'}
                      </p>
                    </button>
                  ))}
                </div>

                <div className="mt-10 text-center p-6 bg-white rounded-2xl border border-slate-100">
                  <p className="text-slate-600 mb-3">Besoin d'aide pour choisir ?</p>
                  <a href="tel:0582819120" className="inline-flex items-center gap-2 text-[#C5A059] font-semibold hover:text-[#B08A45] text-lg">
                    <Phone size={20} />
                    05 82 81 91 20
                  </a>
                  <p className="text-xs text-slate-400 mt-2">Du lundi au vendredi, 9h-18h</p>
                </div>
              </div>
            )}

            {/* Step 2: Personal Information */}
            {step === 2 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <Card className="bg-white border-0 rounded-2xl shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 flex items-center gap-4">
                      {selectedType && (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedType.color} flex items-center justify-center`}>
                          <selectedType.icon size={22} className="text-white" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold text-[#0F2137]">
                          {formData.insuranceType === 'divers' ? 'Autre demande' : `Assurance ${displayLabel}`}
                        </h2>
                        <p className="text-slate-500 text-sm">Étape 2/3 — Vos coordonnées</p>
                      </div>
                    </div>

                    {/* Form */}
                    <div className="p-8 space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-[#0F2137] font-medium flex items-center gap-2">
                            <User size={16} className="text-slate-400" />
                            Nom complet *
                          </Label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Jean Dupont"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#0F2137] font-medium flex items-center gap-2">
                            <Phone size={16} className="text-slate-400" />
                            Téléphone *
                          </Label>
                          <Input
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="06 12 34 56 78"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium flex items-center gap-2">
                          <Mail size={16} className="text-slate-400" />
                          Email *
                        </Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="jean.dupont@email.com"
                          className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium flex items-center gap-2">
                          <MapPin size={16} className="text-slate-400" />
                          Adresse
                        </Label>
                        <Input
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="123 rue de la République"
                          className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-[#0F2137] font-medium">Code postal</Label>
                          <Input
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder="82200"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#0F2137] font-medium">Ville</Label>
                          <Input
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Moissac"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center justify-between pt-4">
                        <Button 
                          type="button"
                          variant="ghost"
                          onClick={() => setStep(1)}
                          className="text-slate-500"
                        >
                          <ArrowLeft size={18} className="mr-2" />
                          Retour
                        </Button>
                        <Button 
                          type="button"
                          onClick={() => {
                            if (formData.name && formData.email && formData.phone) {
                              setStep(3);
                            } else {
                              toast.error("Veuillez remplir tous les champs obligatoires");
                            }
                          }}
                          className="bg-[#C5A059] hover:bg-[#B08A45] text-white px-8 py-6 rounded-xl"
                        >
                          Continuer
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Details */}
            {step === 3 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <Card className="bg-white border-0 rounded-2xl shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 flex items-center gap-4">
                      {selectedType && (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedType.color} flex items-center justify-center`}>
                          <selectedType.icon size={22} className="text-white" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold text-[#0F2137]">
                          {formData.insuranceType === 'divers' ? 'Autre demande' : `Assurance ${displayLabel}`}
                        </h2>
                        <p className="text-slate-500 text-sm">Étape 3/3 — Précisez votre besoin</p>
                      </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-8 space-y-6">
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">
                          Décrivez votre besoin
                        </Label>
                        <Textarea
                          name="details"
                          value={formData.details}
                          onChange={handleChange}
                          rows={6}
                          placeholder="Décrivez ce que vous souhaitez assurer, vos besoins spécifiques, la date souhaitée de prise d'effet..."
                          className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl resize-none"
                        />
                        <p className="text-sm text-slate-500">
                          Plus vous êtes précis, plus notre proposition sera adaptée.
                        </p>
                      </div>

                      {/* Summary */}
                      <div className="p-4 bg-[#F5EFE6] rounded-xl">
                        <h3 className="font-semibold text-[#0F2137] mb-3">Récapitulatif</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Type d'assurance</span>
                            <span className="font-medium text-[#0F2137]">{displayLabel}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium text-[#0F2137]">{formData.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-[#0F2137]">{formData.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium text-[#0F2137]">{formData.phone}</span>
                          </div>
                        </div>
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center justify-between pt-4">
                        <Button 
                          type="button"
                          variant="ghost"
                          onClick={() => setStep(2)}
                          className="text-slate-500"
                        >
                          <ArrowLeft size={18} className="mr-2" />
                          Retour
                        </Button>
                        <Button 
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-[#C5A059] hover:bg-[#B08A45] text-white px-8 py-6 rounded-xl shadow-lg"
                        >
                          {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="animate-in fade-in zoom-in-95 duration-500 text-center py-12">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-[#0F2137] mb-4">Demande envoyée !</h2>
                <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto">
                  Merci pour votre demande. Notre équipe vous contactera sous 24h 
                  avec une proposition personnalisée.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => {
                      setStep(1);
                      setFormData({
                        insuranceType: '', name: '', email: '', phone: '',
                        address: '', city: '', postalCode: '', details: ''
                      });
                    }}
                    variant="outline"
                    className="border-2 border-[#0F2137] text-[#0F2137] px-8 py-6 rounded-xl"
                  >
                    Nouvelle demande
                  </Button>
                  <a href="tel:0582819120">
                    <Button className="bg-[#C5A059] hover:bg-[#B08A45] text-white px-8 py-6 rounded-xl">
                      <Phone size={18} className="mr-2" />
                      Nous appeler
                    </Button>
                  </a>
                </div>
              </div>
            )}

          </div>
        </section>
      </div>
    </>
  );
};
